import { useCallback, useEffect, useState } from 'react';
import type { ScheduledJobSnapshot, SchedulerJobsDebugResponse } from '@ozon-ai/scheduler/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { useTelegram } from '../../../context/TelegramContext';
import { StatusBadge } from '../../../ui/StatusBadge';
import { SETTINGS_DEBUG_HUB } from '../settings-paths';
import {
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

function jobStatusKey(job: ScheduledJobSnapshot): string {
  if (job.running) {
    return 'partial';
  }
  if (job.lastRunStatus === 'success') {
    return 'success';
  }
  if (job.lastRunStatus === 'error') {
    return 'failed';
  }
  return 'draft';
}

function formatDateTime(iso: string | null, timeZone: string): string {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleString('ru-RU', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function jobStatusLabel(job: ScheduledJobSnapshot): string {
  if (job.running) {
    return 'Выполняется';
  }
  if (job.lastRunStatus === 'success') {
    return 'Успех';
  }
  if (job.lastRunStatus === 'error') {
    return 'Ошибка';
  }
  return 'Ещё не запускался';
}

export function SettingsDebugSchedulerPage() {
  const { webApp } = useTelegram();
  const [data, setData] = useState<SchedulerJobsDebugResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<SchedulerJobsDebugResponse>('/debug/scheduler/jobs');
      setData(response);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить расписание');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setLoading(false);
    }
  }, [webApp]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRefresh() {
    await load();
    webApp?.HapticFeedback?.impactOccurred('light');
  }

  async function handleTrigger(jobId: string) {
    setTriggeringId(jobId);
    setError(null);
    try {
      await api.post('/debug/scheduler/jobs/trigger', { jobId });
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить задачу');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setTriggeringId(null);
    }
  }

  const timeZone = data?.timezone ?? 'Europe/Moscow';

  return (
    <SettingsSubPageShell title="Scheduler" backTo={SETTINGS_DEBUG_HUB}>
      <SettingsHint>
        Состояние cron-задач процесса scheduler. «Последний запуск» хранится в памяти сервиса и сбрасывается
        при перезапуске. «Следующий запуск» считается по cron в часовом поясе {timeZone}. Ручной запуск
        выполняет ту же логику, что и cron, и может занять несколько минут.
      </SettingsHint>

      <div className="mt-3 flex flex-col gap-2">
        <Button type="button" disabled={loading} onClick={() => void handleRefresh()}>
          {loading ? 'Обновление…' : 'Обновить'}
        </Button>
        {data?.serverTime && (
          <SettingsHint>Время сервера: {formatDateTime(data.serverTime, timeZone)}</SettingsHint>
        )}
      </div>

      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}

      {data?.jobs.map((job) => (
        <SettingsFormCard key={job.id}>
          <div className="flex flex-col gap-2">
            <p className="font-semibold">{job.title}</p>
            <SettingsHint>{job.description}</SettingsHint>
            <SettingsHint>
              Расписание: {job.scheduleHint} ({job.cron})
            </SettingsHint>
            <StatusBadge status={jobStatusKey(job)}>{jobStatusLabel(job)}</StatusBadge>
            <SettingsHint>Последний запуск: {formatDateTime(job.lastRunAt, timeZone)}</SettingsHint>
            <SettingsHint>Следующий запуск: {formatDateTime(job.nextRunAt, timeZone)}</SettingsHint>
            {job.lastRunError && <p className="text-sm text-destructive">{job.lastRunError}</p>}
            <Button
              type="button"
              className="mt-1 w-fit"
              disabled={loading || job.running || triggeringId != null}
              onClick={() => void handleTrigger(job.id)}
            >
              {triggeringId === job.id || job.running ? 'Выполняется…' : 'Запустить сейчас'}
            </Button>
          </div>
        </SettingsFormCard>
      ))}
    </SettingsSubPageShell>
  );
}
